import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Brain, Dumbbell, Users, Cog, ArrowUpRight } from "lucide-react";
import { PeakMark } from "@/components/PeakMark";

const pillars = [
  { n: "01", icon: Brain, title: "Mindset" },
  { n: "02", icon: Dumbbell, title: "Physical Performance" },
  { n: "03", icon: Users, title: "Leadership & Lifestyle" },
  { n: "04", icon: Cog, title: "Systems" },
];

export const Performance = () => {
  return (
    <section
      id="performance"
      data-testid="performance-section"
      className="relative py-24 md:py-32 bg-[#0A0A0A] text-white overflow-hidden ssg-grain"
    >
      {/* Ambient red glow */}
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full opacity-15 blur-3xl"
        style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16 md:mb-20">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-8">
              <PeakMark size={30} className="text-white/80" />
              <span
                data-testid="performance-overline"
                className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]"
              >
                SSG Performance Coaching
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              data-testid="performance-heading"
              className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-[80px] font-black tracking-tight leading-[1.0]"
            >
              A performance<br />
              operating system<br />
              <span className="italic font-light">for ambitious</span> professionals.
            </motion.h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[15px] md:text-base text-neutral-300 leading-relaxed font-medium max-w-md">
              Not another fitness coach. Not another life coach.{" "}
              <span className="text-white font-semibold">
                A performance coach for people who refuse to be average in
                any area.
              </span>
            </p>
            <p className="mt-5 text-[13px] tracking-wide text-neutral-500 italic">
              Fitness isn&apos;t the goal — it&apos;s the training ground.
            </p>
          </div>
        </div>

        {/* Compact Four Pillars — just icon + title */}
        <div
          data-testid="performance-pillars"
          className="mb-20 md:mb-24 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10"
        >
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                data-testid={`performance-pillar-${i}`}
                className="bg-[#0A0A0A] p-8 lg:p-10 flex flex-col group hover:bg-[#141414] transition-colors"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="relative flex items-center justify-center w-14 h-14">
                    <span className="absolute inset-0 rounded-full border border-[#9B1C1C]/50 group-hover:border-[#E25555] transition-colors" />
                    <span className="absolute inset-2 rounded-full bg-[#9B1C1C]/10 group-hover:bg-[#9B1C1C]/20 transition-colors" />
                    <Icon size={24} strokeWidth={1.5} className="text-white relative" />
                  </div>
                  <span className="font-display text-sm font-bold tracking-[0.2em] text-[#E25555]">
                    /{p.n}
                  </span>
                </div>
                <h3 className="font-display text-2xl lg:text-3xl font-black tracking-tight text-white leading-tight">
                  {p.title}
                </h3>
              </motion.div>
            );
          })}
        </div>

        {/* Training Ground — photo + short copy + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          data-testid="performance-field"
          className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
        >
          <div className="lg:col-span-7 relative">
            <div className="relative aspect-[4/5] md:aspect-[5/6] w-full overflow-hidden bg-neutral-900">
              <img
                src="https://customer-assets.emergentagent.com/job_ssg-coaching/artifacts/onaa07ij_IMG_3810.JPG"
                alt="Brady Smith competing — training as the laboratory for leadership"
                data-testid="performance-field-image"
                className="w-full h-full object-cover object-center contrast-[1.05]"
              />
              <div className="absolute -bottom-px -right-px h-24 w-24 bg-[#9B1C1C]" />
              <div className="absolute top-6 left-6 right-6 flex items-center justify-between text-white">
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold">In the Field</span>
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold">No Compromise</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#E25555]" />
              <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
                I live it before I teach it.
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-white leading-[1.05]">
              Weekly calls.<br />
              A real program.<br />
              <span className="italic font-light">Real accountability</span>.
            </h3>
            <p className="mt-8 text-[15px] leading-relaxed text-neutral-400 font-medium">
              Custom training, mindset work, leadership development, and
              systems design — built around your goal and your life. See the
              full framework, phases, what&apos;s included, and the
              application.
            </p>

            <div className="mt-10">
              <Link
                to="/performance"
                data-testid="performance-cta"
                className="group inline-flex items-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-all duration-300"
              >
                Explore Performance Coaching
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.6}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Performance;
